export interface FormField {
  name: string;
  label: string;
  type?: 'text' | 'number' | 'date' | 'email' | 'tel' | 'select' | 'textarea' | 'password';
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  value?: string | number;
  step?: string;
  min?: string;
  helpText?: string;
  colSpan?: 2;
}

export function createForm(
  fields: FormField[],
  onSubmit: (data: Record<string, string>) => void | Promise<void>,
  submitLabel = 'Save',
  opts?: { cancelAction?: () => void }
): HTMLElement {
  const form = document.createElement('form');
  form.className = 'grid grid-cols-1 md:grid-cols-2 gap-4';

  fields.forEach(f => {
    const group = document.createElement('div');
    if (f.colSpan === 2) group.className = 'md:col-span-2';

    group.innerHTML = `<label class="label">${f.label}${f.required ? ' <span class="text-red-400">*</span>' : ''}</label>`;

    let input: HTMLElement;
    if (f.type === 'select') {
      input = document.createElement('select');
      input.className = 'input-field';
      (input as HTMLSelectElement).innerHTML = `<option value="">Select...</option>` +
        (f.options || []).map(o => `<option value="${o.value}" ${String(f.value) === o.value ? 'selected' : ''}>${o.label}</option>`).join('');
    } else if (f.type === 'textarea') {
      input = document.createElement('textarea');
      input.className = 'input-field';
      (input as HTMLTextAreaElement).rows = 3;
      (input as HTMLTextAreaElement).value = String(f.value || '');
    } else {
      input = document.createElement('input');
      input.className = 'input-field';
      (input as HTMLInputElement).type = f.type || 'text';
      (input as HTMLInputElement).value = String(f.value || '');
      if (f.step) (input as HTMLInputElement).step = f.step;
      if (f.min) (input as HTMLInputElement).min = f.min;
    }

    (input as HTMLInputElement).name = f.name;
    if (f.required) input.setAttribute('required', '');
    if (f.placeholder) (input as HTMLInputElement).placeholder = f.placeholder;

    group.appendChild(input);
    if (f.helpText) {
      const help = document.createElement('p');
      help.className = 'text-xs text-gray-400 mt-1';
      help.textContent = f.helpText;
      group.appendChild(help);
    }
    form.appendChild(group);
  });

  const actions = document.createElement('div');
  actions.className = 'md:col-span-2 flex justify-end gap-3 pt-4 border-t border-gray-100';

  if (opts?.cancelAction) {
    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn-outline';
    cancelBtn.textContent = 'Cancel';
    cancelBtn.addEventListener('click', opts.cancelAction);
    actions.appendChild(cancelBtn);
  }

  const submitBtn = document.createElement('button');
  submitBtn.type = 'submit';
  submitBtn.className = 'btn-primary';
  submitBtn.textContent = submitLabel;
  actions.appendChild(submitBtn);

  form.appendChild(actions);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    submitBtn.disabled = true;
    submitBtn.textContent = 'Saving...';
    try {
      const fd = new FormData(form);
      const data: Record<string, string> = {};
      fd.forEach((v, k) => { data[k] = v as string; });
      await onSubmit(data);
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = submitLabel;
    }
  });

  return form;
}

export function createPageHeader(title: string, actions?: HTMLElement): HTMLElement {
  const header = document.createElement('div');
  header.className = 'page-header';
  header.innerHTML = `<h1 class="text-2xl font-bold text-gray-900">${title}</h1>`;
  if (actions) {
    const actionsWrap = document.createElement('div');
    actionsWrap.className = 'flex gap-2';
    actionsWrap.appendChild(actions);
    header.appendChild(actionsWrap);
  }
  return header;
}
